// LoggedOutHome.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './LoggedOutHome.css';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import logo from '../images/logo.png';

function LoggedOutHome() {
  const navigate = useNavigate();
  const [showFeatures, setShowFeatures] = useState(true);

  const user = JSON.parse(localStorage.getItem('user'));
  const user_id = user?.user_id;

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth' // Use 'smooth' for smooth scrolling, or 'auto' for instant
    });
  }, []);
  
  // Use useEffect for navigation
  useEffect(() => {
    if (user_id) {
      navigate('/home');
    }
  }, [user_id, navigate]);

  const features = [
    {
      icon: "bi-chat-text",
      title: "AI Conversations",
      description: "Chat with advanced AI that understands context and provides helpful responses"
    },
    {
      icon: "bi-image",
      title: "Image Analysis",
      description: "Upload images and get detailed descriptions or ask questions about them"
    },
    {
      icon: "bi-code-slash",
      title: "Code Assistant",
      description: "Write, debug, and explain code in multiple programming languages"
    },
    {
      icon: "bi-shield-check",
      title: "Secure & Private",
      description: "Your conversations are encrypted and only you can access them"
    }
  ];

  return (
    <div className="logged-out-container min-vh-100 d-flex flex-column">
      {/* Fixed Navigation */}
      <nav className="navbar navbar-expand-lg navbar-dark py-3 fixed-top">
        <div className="container">
          <button
            className="navbar-brand d-flex align-items-center gap-2 btn btn-link text-decoration-none p-0 border-0"
            onClick={() => navigate('/')}
          >
            <img src={logo} alt='Marshall AI Logo' className='logo' />
            <span className="fs-4 fw-bold text-light">Marshall AI</span>
          </button>
          <div className="d-flex gap-3">
            <button
              className="btn btn-outline-primary"
              onClick={() => navigate('/login')}
            >
              Login
            </button>
            <button
              className="btn btn-dark"
              onClick={() => navigate('/signup')}
            >
              Sign Up
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content with padding-top for fixed navbar */}
      <div className="main-content" style={{ paddingTop: '76px' }}>
        {/* Hero Section */}
        <header className="hero-section text-center py-5">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-8">
                <h1 className="display-3 fw-bold text-light mb-4">
                  Intelligent Conversations
                  <span className="text-primary"> Reimagined</span>
                </h1>
                <p className="lead text-light mb-5 opacity-75">
                  Experience the power of AI with our advanced chatbot. Get instant help with writing,
                  coding, analysis, image generation, and much more.
                </p>

                <div className="cta-buttons d-flex gap-3 justify-content-center">
                  <button
                    className="btn btn-primary btn-lg px-5 py-3 fw-semibold"
                    onClick={() => navigate('/signup')}
                  >
                    <i className="bi bi-lightning-charge-fill me-2"></i>
                    Get Started Free
                  </button>
                  <button
                    className="btn btn-outline-light btn-lg px-5 py-3"
                    onClick={() => setShowFeatures(!showFeatures)}
                  >
                    <i className="bi bi-info-circle me-2"></i>
                    {showFeatures ? 'Hide Features' : 'Explore Features'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Demo Preview Section */}
        <section className="demo-section py-5">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-6 mb-4 mb-lg-0">
                <div className="demo-chat p-4 rounded-4">
                  {/* Chat Header */}
                  <div className="d-flex align-items-center mb-4">
                    <div className="demo-avatar me-3">
                      <i className="bi bi-robot fs-3 text-primary"></i>
                    </div>
                    <div>
                      <h5 className="text-light mb-1">AI Assistant</h5>
                      <small className="text-muted">Online • Always Ready to Help</small>
                    </div>
                  </div>

                  {/* Chat Messages */}
                  <div className="message user-message p-3 mb-3 rounded-3">
                    <div className="d-flex align-items-center mb-2">
                      <div className="user-avatar me-2">
                        <i className="bi bi-person-circle text-primary"></i>
                      </div>
                      <strong className="text-light">You</strong>
                    </div>
                    <p className="mb-0 text-light">Can you help me write a Python function to calculate factorial?</p>
                  </div>

                  <div className="message bot-message p-3 rounded-3">
                    <div className="d-flex align-items-center mb-2">
                      <div className="bot-avatar me-2">
                        <i className="bi bi-robot text-success"></i>
                      </div>
                      <strong className="text-light">AI Assistant</strong>
                    </div>
                    <pre className="code-block">
                      {`def factorial(n):
    """Calculate factorial of a number"""
    if n < 0:
        return "Factorial not defined for negative numbers"
    elif n == 0 or n == 1:
        return 1
    else:
        result = 1
        for i in range(2, n + 1):
            result *= i
        return result

# Example usage
print(factorial(5))  # Output: 120`}
                    </pre>
                  </div>

                  {/* Typing Indicator */}
                  <div className="typing-indicator mt-3">
                    <div className="d-flex align-items-center">
                      <div className="typing-dots">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                      <span className="text-muted ms-2">AI is thinking...</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-lg-6">
                <div className="ps-lg-5">
                  <h2 className="text-light mb-4">Why Choose Our AI Assistant?</h2>

                  <div className="feature-list">
                    {[
                      "🎯 Context-aware conversations",
                      "🚀 Instant responses",
                      "🔒 Secure & private chats",
                      "💾 Chat history saved",
                      "🖼️ Image upload & analysis"
                    ].map((feature, index) => (
                      <div key={index} className="d-flex align-items-center mb-3">
                        <i className="bi bi-check-circle-fill text-success me-3"></i>
                        <span className="text-light">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <button
                    className="btn btn-primary btn-lg mt-4 px-5"
                    onClick={() => navigate('/signup')}
                  >
                    <i className="bi bi-rocket-takeoff me-2"></i>
                    Start Your Journey
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        {showFeatures && (
          <section className="features-section py-5">
            <div className="container">
              <div className="row mb-5">
                <div className="col-12 text-center">
                  <h2 className="text-light mb-3">Everything You Need</h2>
                  <p className="text-muted">Powerful features at your fingertips</p>
                </div>
              </div>

              <div className="row g-4">
                {features.map((feature, index) => (
                  <div key={index} className="col-md-6 col-lg-4">
                    <div className="feature-card h-100 p-4 rounded-4">
                      <div className="feature-icon mb-3">
                        <i className={`bi ${feature.icon} fs-1 text-primary`}></i>
                      </div>
                      <h5 className="text-light mb-2">{feature.title}</h5>
                      <p className="text-muted mb-0">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Call to Action */}
        <section className="cta-section py-5">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-8 text-center">
                <h2 className="text-light mb-4">Ready to Get Started?</h2>
                <p className="text-light mb-5 opacity-75">
                  Join thousands of users who are already experiencing the future of conversations.
                  No credit card required for the free trial.
                </p>
                <div className="d-flex gap-3 justify-content-center">
                  <button
                    className="btn-lg px-5 btn btn-primary account"
                    onClick={() => navigate('/signup')}
                  >
                    <i className="bi bi-person-plus me-2"></i>
                    Create Free Account
                  </button>
                  <button
                    className="btn btn-outline-primary btn-lg px-5"
                    onClick={() => navigate('/login')}
                  >
                    <i className="bi bi-box-arrow-in-right me-2"></i>
                    Login to Continue
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="footer-section mt-auto">
          <div className="footer-main">
            <div className="container">
              <div className="row g-4">
                {/* Company Info Column */}
                <div className="col-lg-5 col-md-6">
                  <div className="footer-brand mb-3">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <img src={logo} alt='Marshall AI Logo' className='footer-logo' />
                      <span className="footer-brand-name">Marshall AI</span>
                    </div>
                    <p className="footer-description">
                      Experience the future of conversations with our advanced AI assistant.
                      Get instant help with writing, coding, analysis, and creative tasks.
                    </p>
                  </div>
                  <div className="social-links mb-3">
                    <a href="https://www.linkedin.com/in/aman-rajput-2b7680316/" target="_blank" rel="noopener noreferrer" className="social-link linkedin">
                      <i className="bi bi-linkedin"></i>
                    </a>
                    <a href="https://github.com/rajput81325/" target="_blank" rel="noopener noreferrer" className="social-link github">
                      <i className="bi bi-github"></i>
                    </a>
                    <a href="https://instagram.com/rajputaman625" target="_blank" rel="noopener noreferrer" className="social-link instagram">
                      <i className="bi bi-instagram"></i>
                    </a>
                  </div>
                </div>

                {/* Links Column - All links in one */}
                <div className="col-lg-3 col-md-6">
                  <h5 className="footer-heading">Quick Links</h5>
                  <ul className="footer-links">
                    <li><a href="/docs">Documentation</a></li>
                    <li><a href="/about">About Us</a></li>
                    <li><a href="/contact">Contact</a></li>
                  </ul>
                </div>

                {/* Newsletter Column */}
                <div className="col-lg-4 col-md-6">
                  <h5 className="footer-heading">Stay Updated</h5>
                  <p className="footer-text">Get the latest updates and news</p>
                  <div className="newsletter-form">
                    <div className="input-group">
                      <input
                        type="email"
                        className="form-control"
                        placeholder="Your email"
                        aria-label="Email"
                      />
                      <button className="btn btn-primary" type="button">
                        <i className="bi bi-send"></i>
                      </button>
                    </div>
                  </div>
                  <p className="footer-small-text mt-2">
                    <i className="bi bi-shield-check me-1"></i>
                    We care about your privacy
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className="footer-bottom">
            <div className="container">
              <div className="row align-items-center">
                <div className="col-md-6">
                  <p className="copyright mb-0">
                    &copy; {new Date().getFullYear()} Marshall AI. All rights reserved.
                  </p>
                </div>
                <div className="col-md-6">
                  <div className="footer-bottom-links">
                    <a href="/privacy">Privacy Policy</a>
                    <span className="divider">|</span>
                    <a href="/terms">Terms of Service</a>
                    <span className="divider">|</span>
                    <a href="/cookies">Cookie Policy</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>

      <ToastContainer />
    </div>
  );
}

export default LoggedOutHome;